import {
  Component, OnInit, Input, Output, EventEmitter, ViewChild, OnDestroy, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { ImageIndividual } from '../models/imageIndividual';
import { RankCategory } from '../models/rankCategory';
import { Rank } from '../models/rank';
import { Regiment } from '../models/regiment';
import { BranchOfService } from '../models/branchOfService';
import { State } from '../models/state';
import { WriteOrdinalSuffixOfNumber, NumericSortArrayByProperty } from 'starbase-launcher';
import { UnitPickerComponent } from '../unit-picker/unit-picker.component';
import { StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalButton, StarbaseModalTypes } from 'starbase-modal-dialog';
import { LookupDataService } from '../services/lookup-data.service';
import { Subscription, Observable, of, Subject, merge } from 'rxjs';
import { debounceTime, switchMap, distinctUntilChanged, filter, map, timeout, finalize } from 'rxjs/operators';
import { ImageTypes } from '../models/imageType';
import { NgbTypeaheadSelectItemEvent, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';

const DEFAULT_DEBOUNCE_TIME = 275; // ms
const TYPEAHEAD_RESULTS_LIMIT = 5;

@Component({
  selector: 'app-individuals-entry',
  templateUrl: './individuals-entry.component.html',
  styleUrls: ['./individuals-entry.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IndividualsEntryComponent implements OnInit, OnDestroy {

  @Input() typeOfImageId: ImageTypes;
  @Input() data: ImageIndividual = new ImageIndividual();
  @Input() rankCats: RankCategory[] = [];
  @Input() regiments: Regiment[] = [];
  @Input() states: State[] = [];
  @Input() branches: BranchOfService[] = [];
  @Input() coLetters = '';
  @Input() showIndividual = false;
  @Input() editMode = false; // if false, this is 'Add' mode.
  @Input() dropdownTypeDebounceTime = 900; // ms

  @Output() doneAdding: EventEmitter<ImageIndividual> = new EventEmitter<ImageIndividual>();
  @Output() doneEditing: EventEmitter<ImageIndividual> = new EventEmitter<ImageIndividual>();
  @Output() cancel: EventEmitter<any> = new EventEmitter<any>();
  @Output() delete: EventEmitter<ImageIndividual> = new EventEmitter<ImageIndividual>();

  @ViewChild('unitPicker') unitPicker: UnitPickerComponent;
  @ViewChild('fNameSearcher') fNameSearcher: NgbTypeahead;
  @ViewChild('lNameSearcher') lNameSearcher: NgbTypeahead;
  // @ViewChild('firstFocus', { static: true }) firstFocus: ElementRef;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ImageTypes = ImageTypes;

  public ranks: Rank[] = [];
  public ranksFiltered: Rank[] = [];
  public currentRankCat = '';
  public loaded = false;

  public allIndividualsFirstNames: string[] = [];
  public allIndividualsLastNames: string[] = [];

  writeOrdinalSuffix = WriteOrdinalSuffixOfNumber;

  ignoreTypeaheadSuggestion = false;

  fNameFocus$ = new Subject<string>();
  fNameClick$ = new Subject<string>();
  lNameFocus$ = new Subject<string>();
  lNameClick$ = new Subject<string>();

  loadedNames: boolean;
  loadedRanks: boolean;

  private rankCatUsed = -2;
  private imgIndSubscriber: Subscription;
  private ranksSubscriber: Subscription;

  constructor(private firstFocus: ElementRef,
    private cd: ChangeDetectorRef,
    private modalSvc: StarbaseModalDialogService,
    private lookupSvc: LookupDataService) { }

  ngOnInit() {

    this.ranksSubscriber = this.lookupSvc.getAllRequisiteData<Rank>(this.lookupSvc.getRanksUrl).pipe(
      map(ranks => {
        this.ranks = ranks; Rank.setupRankCollection(this.ranks);
      })).subscribe(() => {
        this.loadedRanks = true;
        this.checkDoneLoading();
      });

    this.getTypeaheadData();

    this.regiments = NumericSortArrayByProperty(this.regiments, 'regimentNo');
    // setTimeout(() =>
    //     this.renderer.selectRootElement('#firstNameInput').focus(), 0);

  }
  ngOnDestroy() {
    if (this.imgIndSubscriber) {
      this.imgIndSubscriber.unsubscribe();
    }
    if (this.ranksSubscriber) {
      this.ranksSubscriber.unsubscribe();
    }
  }

  checkDoneLoading(): void {
    if (this.loaded) {
 return;
}
    if (this.loadedNames && this.loadedRanks) {
      this.loaded = true;

      if (this.data.uid === 0 && this.data.firstName === undefined && this.data.lastName === undefined && this.data.notes === undefined) {
        this.reset(); // clear form for new data
      } else {
        if (!this.data.rank || this.data.rank.uid === 0 || this.data.rank.rankCategoryId < 1 || this.data.rank.rankCategoryId === null) {
          this.resetRankToNone();
        } else {
          this.filterRanksByCat(this.data.rank.rankCategoryId);
          this.data.rank = (this.data.rank.rankCategoryId === -1) ? new Rank() : this.ranksFiltered.find(r => r.uid === this.data.rankId);
        }
      }
      this.cd.markForCheck();
      setTimeout(() => {
        this.firstFocus.nativeElement.querySelector('.focus').focus();
        // setup unit data
        if (!this.data.regimentNo) {
          this.data.regimentNo = new Regiment();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedRegiment(this.regiments.find(r => r.uid === this.data.regimentNo.uid));
          }, 100);
        }
        if (!this.data.corps) {
          this.data.corps = null;
        } else {
          this.unitPicker.setSelectedCorps(this.data.corps);
        }
        if (!this.data.army) {
          this.data.army = 0;
        } else {
          this.unitPicker.setSelectedUnitArmy(this.data.army);
        }
        if (!this.data.state) {
          this.data.state = new State();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedState(this.states.find(s => s.uid === this.data.state.uid));
          }, 100);
        }
        if (!this.data.branch) {
          this.data.branch = new BranchOfService();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedBranch(this.branches.find(b => b.uid === this.data.branch.uid));
          }, 100);
        }
        if (this.data.companyLetters) {
          setTimeout(() => {
            this.unitPicker.setSelectedCoLetters(this.data.companyLetters.split(','));
          }, 100);
        }
        this.cd.markForCheck();
      }, 750);
    }
  }

  nameSearch(first: boolean = true): (text$: Observable<string>) => Observable<any[]> {
    return (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(
        debounceTime(DEFAULT_DEBOUNCE_TIME),
        distinctUntilChanged());
      const clicksWithClosedPopups$ = first
        ? this.fNameClick$.pipe(
          filter(() => !this.fNameSearcher.isPopupOpen()))
        : this.lNameClick$.pipe(filter(() => !this.lNameSearcher.isPopupOpen()));
      const inputFocus$ = first ? this.fNameFocus$ : this.lNameFocus$;
      return merge(debouncedText$, inputFocus$, clicksWithClosedPopups$).pipe(
        switchMap(term => {
          if (term.length < 2) {
            return of([]);
          } else if (first) {
            return of(this.allIndividualsFirstNames.filter(
              f => f.toLowerCase()
                .indexOf(term.toLowerCase()) > -1)
                .slice(0, TYPEAHEAD_RESULTS_LIMIT));
          } else {
            return of(this.allIndividualsLastNames.filter(
              f => f.toLowerCase()
                .indexOf(term.toLowerCase()) > -1)
                .slice(0, TYPEAHEAD_RESULTS_LIMIT));
          }
        })
      );
    };
  }
  // lastNameSearch = (text$: Observable<string>) =>
  //   text$.pipe(
  //     debounceTime(DEFAULT_DEBOUNCE_TIME),
  //     distinctUntilChanged(),
  //     switchMap(term => term.length < 2 ? []
  //       : of(this.allIndividualsLastNames.filter(f => f.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, TYPEAHEAD_RESULTS_LIMIT)))
  //   )

  interceptTypeaheadSelection(event: NgbTypeaheadSelectItemEvent) {
    if (this.ignoreTypeaheadSuggestion) {
      event.preventDefault();
      this.ignoreTypeaheadSuggestion = false;
    }
  }

  filterRanksByCat(rankCatId: number) {
    // eslint-disable-next-line curly
    if (!rankCatId) rankCatId = -1;
    this.ranksFiltered = []; // reset the array

    // eslint-disable-next-line eqeqeq
    this.ranksFiltered = this.ranks.filter(r => r.rankCategoryId == rankCatId);
    this.data.rank = new Rank();
    this.data.rank.rankCategoryId = rankCatId;
    this.rankCatUsed = rankCatId;
    this.cd.markForCheck();
  }
  updateRank(rankId: number) {
    const findRank = this.ranks.find(r => r.uid === rankId);
    if (!findRank) {
      this.data.rank = new Rank();
    } else {
      this.data.rank = new Rank().deserialize(findRank);
    }
    this.data.rank.rankCategory = new RankCategory().deserialize(this.data.rank.rankCategory);
    this.data.rank.rankCategoryId = this.data.rank.rankCategory.uid;
    this.cd.markForCheck();
  }
  updateUnit(unit) {
    this.data.army = unit.army;
    this.data.corps = unit.corps;
    this.data.regimentNoId = (unit.regiment) ? unit.regiment.uid : null;
    this.data.regimentNo = unit.regiment;
    this.data.stateId = (unit.state) ? unit.state.uid : null;
    this.data.state = unit.state;
    this.data.branchId = (unit.branch) ? unit.branch.uid : null;
    this.data.branch = unit.branch;
    this.data.companyLetters = (unit.coLetters) ? unit.coLetters.join() : '';
    this.cd.markForCheck();
  }
  reset() {
    // this.data = new ImageIndividual();
    this.data.firstName = '';
    this.data.lastName = '';
    this.data.currentRankInPhoto = false;
    this.data.notes = '';
    Rank.setupRankCollection(this.ranks);
    this.data.rank = new Rank();
    this.data.rankId = null;
    this.data.rank.rankCategory = new RankCategory();
    this.data.rank.rankCategoryId = -1;
    this.data.regimentNo = new Regiment();
    this.data.state = new State();
    this.data.branch = new BranchOfService();
    this.data.companyLetters = '';
    this.data.army = 0;
    this.data.corps = null;
    this.ranksFiltered = this.ranks;
    this.cd.markForCheck();
  }
  saveButton(): void {
    const doEmit = () => {
      if (this.editMode) {
        this.doneEditing.emit(this.data);
      } else {
        this.doneAdding.emit(this.data);
      }
    };
    const checkName = () => {
      // prompt user if they did not enter a first name or a last name
      if (this.data.firstName === '' && this.data.lastName === '') {
        const anonModal = new StarbaseModalOptions(
          'Save Anonymous Individual?',
          'Are you sure you want to save this individual without a name?',
          [
            new StarbaseModalButton('Yes', () => {
              doEmit();
              this.modalSvc.closeModal('User pressed Yes');
            }),
            new StarbaseModalButton('No', () => {
              this.modalSvc.closeModal('User pressed No');
              if (this.rankCatUsed > -2) {
                this.filterRanksByCat(this.rankCatUsed);
              }
            this.loaded = true;
            this.firstFocus.nativeElement.focus();
          })
        ], StarbaseModalTypes.Warning);
        anonModal.dismissable = false;
        this.modalSvc.postModal(anonModal);
      } else {
        doEmit();
      }
    };
    const checkRank = () => {
      // prompt user if they did not choose a Rank for their Rank Cat.
      // checks if rankCatUsed is greater than -2 AND the RankId is less than 1,
      // meaning a rank category was chosen, but no rank within that category was selected.
      if (this.rankCatUsed > -2 && this.data.rankId < 1) {
        const rankModal = new StarbaseModalOptions(
          'Eschew Rank Information?',
          'You chose a rank category without selecting a rank. No rank data will be saved for this individual. Proceed anyway?',
          [
            new StarbaseModalButton('Yes', () => {
              checkName();
              this.modalSvc.closeModal('User pressed Yes');
              this.data.rank = null; // set rank to null
              this.data.rankId = null; // set rankId to null
          }),
          new StarbaseModalButton('No', () => {
            this.modalSvc.closeModal('User pressed No');
            this.filterRanksByCat(this.rankCatUsed);
            this.loaded = true;
          })
        ], StarbaseModalTypes.Warning);
        rankModal.dismissable = false;
        this.modalSvc.postModal(rankModal);
      } else {
        if (this.data.rankId < 1) {
          this.data.rankId = null;
        }
        checkName();
      }
    };

    this.loaded = false;
    this.data.rankId = (this.data.rankId && this.data.rankId !== 0) ? this.data.rankId : -1;
    this.data.rank = (this.data.rank && this.data.rank.uid !== 0) ? this.data.rank : null; // instead of new Rank()
    this.data.regimentNo = (this.data.regimentNo && this.data.regimentNo.uid !== 0) ? this.data.regimentNo : null;
    this.data.state = (this.data.state && this.data.state.uid !== 0) ? this.data.state : null;
    this.data.branch = (this.data.branch && this.data.branch.uid !== 0) ? this.data.branch : null;
    this.data.companyLetters = (this.data.companyLetters === '*') ? '' : this.data.companyLetters;
    checkRank();
  }

  deleteButton() {
    this.delete.emit(this.data);
  }

  close(): void {
    this.showIndividual = false;
    this.loaded = false;
    this.loadedRanks = false;
    this.loadedNames = false;
    this.cancel.emit();
  }

  private resetRankToNone(): void {
    this.data.rank = new Rank();
    this.data.rankId = null;
    this.data.rank.rankCategory = new RankCategory();
    this.data.rank.rankCategoryId = -1;
  }

  // server must respond within 22 seconds, or timeout error occurs and component will load without typeahead support.
  private getTypeaheadData() {
    this.imgIndSubscriber = this.lookupSvc.getAllImageIndividualsFromServer().pipe(
      timeout(22000),
      finalize(() => {
      this.loadedNames = true;
      this.checkDoneLoading();
    })).subscribe(i => {
        i.map(ii => {
          this.allIndividualsFirstNames.push(ii.firstName);
          this.allIndividualsLastNames.push(ii.lastName);
        });
      },
      () => {
        console.error('Could not load individuals name data.');
      });
  }

}
